import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Loader,
  Radio,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import AuthCard from '@/components/auth/AuthCard';
import PasswordField from '@/components/auth/PasswordField';
import RegisterSuccess from '@/components/auth/RegisterSuccess';
import { userManagementService } from '@/api/services/UserManagementService';
import { getConflictReason, getReadableApiMessage, isNetworkError, isServiceUnavailable } from '@/api/errors';
import { useInnAutofill, type InnRejection } from '@/hooks/useInnAutofill';
import { useCooldown } from '@/hooks/useCooldown';
import { focusField, focusFirstError } from '@/lib/focusField';
import { formatPhone, isValidPhone, normalizePhone } from '@/lib/phone';
import {
  EMAIL_PATTERN,
  NO_SPACES_PATTERN,
  PASSWORD_MIN_LENGTH,
  isBlank,
  isValidInn,
} from '@/lib/validators';
import { ROUTES } from '@/routes/paths';
import type { RegisterFormValues, RegisterRequest, UserType } from '@/types/auth';

const INITIAL: RegisterFormValues = {
  registrationType: '',
  inn: '',
  phone: '',
  lastName: '',
  firstName: '',
  patronymic: '',
  name: '',
  contact: '',
  email: '',
  password: '',
  confirmPassword: '',
  acceptPersonal: false,
  acceptTerms: false,
};

// Смысловая группа полей формы. Заголовки разбивают длинный список, состав полей не меняется.
function FormSection({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <Stack gap="sm" component="section">
      <Text component="h2" fw={600} size="sm" c="brand.9" m={0}>
        {title}
      </Text>
      {children}
    </Stack>
  );
}

export default function Register() {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);
  const resend = useCooldown(30);
  const formErrorRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // Значения, отклонённые сервером: ошибка держится, пока значение не изменится (иначе её снимет проверка при уходе с поля).
  const rejected = useRef<Partial<Record<'inn' | 'email', InnRejection>>>({});
  const innRejection = useRef<InnRejection | null>(null);

  function rejectedMessage(path: 'inn' | 'email', value: string): string | null {
    const entry = rejected.current[path];
    return entry?.value === value ? entry.message : null;
  }

  function rejectValue(path: 'inn' | 'email', value: string, message: string): void {
    rejected.current[path] = { value, message };
    form.setFieldError(path, message);
  }

  const form = useForm<RegisterFormValues>({
    mode: 'controlled',
    initialValues: INITIAL,
    validate: {
      registrationType: (value) => (value ? null : t('auth.validation.typeRequired')),
      inn: (value, values) => {
        const type = values.registrationType || null;
        if (!type) {
          return null; // поле ещё закрыто: ошибку покажет выбор «Кто регистрируется»
        }
        if (isBlank(value)) {
          return t('auth.validation.required');
        }
        if (isValidInn(value, type)) {
          const check = innRejection.current;
          return check?.value === value ? check.message : rejectedMessage('inn', value);
        }
        return type === 'LEGAL_ENTITY'
          ? t('auth.validation.innLegal')
          : t('auth.validation.innSelf');
      },
      phone: (value) =>
        isBlank(value)
          ? t('auth.validation.required')
          : isValidPhone(value)
            ? null
            : t('auth.validation.phone'),
      email: (value) =>
        isBlank(value)
          ? t('auth.validation.required')
          : EMAIL_PATTERN.test(value.trim())
            ? rejectedMessage('email', value.trim())
            : t('auth.validation.email'),
      password: (value) =>
        isBlank(value)
          ? t('auth.validation.required')
          : value.length < PASSWORD_MIN_LENGTH
            ? t('auth.validation.passwordMin')
            : !NO_SPACES_PATTERN.test(value)
              ? t('auth.validation.passwordSpaces')
              : null,
      confirmPassword: (value, values) =>
        isBlank(value)
          ? t('auth.validation.required')
          : value !== values.password
            ? t('auth.validation.passwordMismatch')
            : null,
      lastName: (value, values) =>
        values.registrationType === 'SELF_EMPLOYED' && isBlank(value)
          ? t('auth.validation.required')
          : null,
      firstName: (value, values) =>
        values.registrationType === 'SELF_EMPLOYED' && isBlank(value)
          ? t('auth.validation.required')
          : null,
      name: (value, values) =>
        values.registrationType === 'LEGAL_ENTITY' && isBlank(value)
          ? t('auth.validation.required')
          : null,
      contact: (value, values) =>
        values.registrationType === 'LEGAL_ENTITY' && isBlank(value)
          ? t('auth.validation.required')
          : null,
      acceptPersonal: (value) => (value ? null : t('auth.validation.acceptRequired')),
      acceptTerms: (value) => (value ? null : t('auth.validation.acceptRequired')),
    },
    validateInputOnBlur: true,
  });

  const registrationType = form.values.registrationType;
  const innValue = form.values.inn;
  const isLegal = registrationType === 'LEGAL_ENTITY';
  const isSelf = registrationType === 'SELF_EMPLOYED';
  const innMaxLength = isLegal ? 10 : 12;
  const {
    checking: innChecking,
    nameLocked,
    checkFailed: innCheckFailed,
    retry: retryInnCheck,
  } = useInnAutofill({ form, type: registrationType, inn: innValue, rejection: innRejection });

  // Смена типа: сбросить зависимые поля и обрезать ИНН под новую длину.
  useEffect(() => {
    form.setValues({ lastName: '', firstName: '', patronymic: '', name: '', contact: '' });
    form.clearFieldError('inn');
    const max = registrationType === 'LEGAL_ENTITY' ? 10 : 12;
    form.setFieldValue('inn', form.getValues().inn.slice(0, max));
    // Зависимость осознанно только от типа: form стабилен, поля читаем через getValues().
  }, [registrationType]);

  // Ошибка общая, а не поля: показываем у кнопки отправки и переводим к ней фокус, иначе её не увидят.
  useEffect(() => {
    if (formError) {
      formErrorRef.current?.focus();
    }
  }, [formError]);

  function handleInvalid(errors: Record<string, unknown>): void {
    setFormError(null); // прежняя причина уже не актуальна: сейчас мешает ошибка поля
    focusFirstError(formRef.current, errors);
  }

  async function handleSubmit(values: RegisterFormValues): Promise<void> {
    setSubmitting(true);
    setFormError(null);
    try {
      const payload: RegisterRequest = {
        userType: values.registrationType as UserType,
        inn: values.inn,
        email: values.email.trim(),
        phone: normalizePhone(values.phone),
        password: values.password,
        firstName: isLegal ? undefined : values.firstName.trim() || undefined,
        lastName: isLegal ? undefined : values.lastName.trim() || undefined,
        patronymicName: isLegal ? undefined : values.patronymic.trim() || undefined,
        name: isLegal ? values.name.trim() || undefined : undefined,
        contact: isLegal ? values.contact.trim() || undefined : undefined,
      };
      await userManagementService.register(payload);
      setSuccessEmail(values.email.trim());
    } catch (error) {
      const conflict = getConflictReason(error);
      if (conflict === 'inn' || conflict === 'email') {
        const value = conflict === 'email' ? values.email.trim() : values.inn;
        rejectValue(conflict, value, t(`auth.register.conflict.${conflict}`));
        focusField(form.getInputNode(conflict));
      } else if (isNetworkError(error) || isServiceUnavailable(error)) {
        setFormError(t('auth.errors.unavailable'));
      } else {
        setFormError(getReadableApiMessage(error) ?? t('auth.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    if (!successEmail) {
      return;
    }
    try {
      await userManagementService.resendRegistrationEmail(successEmail);
      resend.start();
      notifications.show({ color: 'lime', message: t('auth.register.resent') });
    } catch {
      notifications.show({ color: 'red', message: t('auth.errors.generic') });
    }
  }

  const innDescription = !registrationType ? (
    t('auth.register.innHint')
  ) : innCheckFailed ? (
    <>
      {t('auth.register.innCheckFailed')}{' '}
      <Anchor component="button" type="button" size="sm" c="brand.7" underline="always" onClick={retryInnCheck}>
        {t('auth.register.innRetry')}
      </Anchor>
    </>
  ) : undefined;

  if (successEmail) {
    return (
      <RegisterSuccess
        email={successEmail}
        resendActive={resend.active}
        resendRemaining={resend.remaining}
        onResend={handleResend}
      />
    );
  }

  return (
    <AuthCard
      title={t('auth.register.title')}
      description={t('auth.register.description')}
      footer={
        <Text size="sm">
          {t('auth.register.haveAccount')}{' '}
          <Anchor component={Link} to={ROUTES.LOGIN} c="brand.7" underline="always">
            {t('auth.register.loginLink')}
          </Anchor>
        </Text>
      }
    >
      <form ref={formRef} onSubmit={form.onSubmit(handleSubmit, handleInvalid)} noValidate>
        <Stack gap="lg">
          <FormSection title={t('auth.register.sectionOrg')}>
            <Radio.Group
              label={t('auth.register.typeLabel')}
              withAsterisk
              key={form.key('registrationType')}
              {...form.getInputProps('registrationType')}
            >
              <Stack gap="xs" mt="xs">
                <Radio value="SELF_EMPLOYED" label={t('auth.register.typeSelf')} />
                <Radio value="LEGAL_ENTITY" label={t('auth.register.typeLegal')} />
              </Stack>
            </Radio.Group>

            <TextInput
              label={t('auth.fields.inn')}
              inputMode="numeric"
              maxLength={innMaxLength}
              disabled={!registrationType}
              description={innDescription}
              required
              rightSection={innChecking ? <Loader size="xs" /> : null}
              key={form.key('inn')}
              {...form.getInputProps('inn')}
              onChange={(event) =>
                form.setFieldValue(
                  'inn',
                  event.currentTarget.value.replace(/\D/g, '').slice(0, innMaxLength),
                )
              }
            />

            {isSelf ? (
              <>
                <TextInput
                  label={t('auth.fields.lastName')}
                  autoComplete="family-name"
                  required
                  key={form.key('lastName')}
                  {...form.getInputProps('lastName')}
                />
                <TextInput
                  label={t('auth.fields.firstName')}
                  autoComplete="given-name"
                  required
                  key={form.key('firstName')}
                  {...form.getInputProps('firstName')}
                />
                <TextInput
                  label={t('auth.fields.patronymic')}
                  autoComplete="additional-name"
                  key={form.key('patronymic')}
                  {...form.getInputProps('patronymic')}
                />
              </>
            ) : null}

            {isLegal ? (
              <TextInput
                label={t('auth.fields.orgName')}
                autoComplete="organization"
                required
                readOnly={nameLocked}
                description={nameLocked ? t('auth.register.nameFromRegistry') : undefined}
                key={form.key('name')}
                {...form.getInputProps('name')}
              />
            ) : null}
          </FormSection>

          <FormSection title={t('auth.register.sectionContacts')}>
            {isLegal ? (
              <TextInput
                label={t('auth.fields.contact')}
                autoComplete="name"
                required
                key={form.key('contact')}
                {...form.getInputProps('contact')}
              />
            ) : null}

            <TextInput
              label={t('auth.fields.email')}
              type="email"
              autoComplete="email"
              required
              key={form.key('email')}
              {...form.getInputProps('email')}
            />

            <TextInput
              label={t('auth.fields.phone')}
              type="tel"
              autoComplete="tel"
              placeholder="+7 (___) ___-__-__"
              required
              key={form.key('phone')}
              {...form.getInputProps('phone')}
              onChange={(event) => form.setFieldValue('phone', formatPhone(event.currentTarget.value))}
            />

          </FormSection>

          <FormSection title={t('auth.register.sectionPassword')}>
            <PasswordField
              label={t('auth.fields.password')}
              autoComplete="new-password"
              required
              key={form.key('password')}
              {...form.getInputProps('password')}
            />
            <PasswordField
              label={t('auth.fields.confirmPassword')}
              autoComplete="new-password"
              required
              key={form.key('confirmPassword')}
              {...form.getInputProps('confirmPassword')}
            />
          </FormSection>

          <FormSection title={t('auth.register.sectionConsents')}>
            <Checkbox
              label={
                <>
                  {t('auth.register.acceptPrefix')}{' '}
                  <Anchor
                    component={Link}
                    to={ROUTES.POLICY}
                    target="_blank"
                    rel="noopener noreferrer"
                    c="brand.7"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {t('auth.register.policyLink')}
                  </Anchor>
                </>
              }
              required
              key={form.key('acceptPersonal')}
              {...form.getInputProps('acceptPersonal', { type: 'checkbox' })}
            />
            <Checkbox
              label={
                <>
                  {t('auth.register.acceptPrefix')}{' '}
                  <Anchor
                    component={Link}
                    to={ROUTES.TERMS}
                    target="_blank"
                    rel="noopener noreferrer"
                    c="brand.7"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {t('auth.register.termsLink')}
                  </Anchor>
                </>
              }
              required
              key={form.key('acceptTerms')}
              {...form.getInputProps('acceptTerms', { type: 'checkbox' })}
            />
          </FormSection>

          {formError ? (
            <Alert ref={formErrorRef} color="red" variant="light" tabIndex={-1}>
              {formError}
            </Alert>
          ) : null}

          <Button type="submit" loading={submitting} fullWidth>
            {t('auth.register.submit')}
          </Button>
        </Stack>
      </form>
    </AuthCard>
  );
}
